import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Collapse } from '@mui/material';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import {
  getPushNotificationPermission,
  isPushNotificationConfigured,
  registerCurrentDeviceForPush,
} from '../../services/notifications/pushNotifications';

const ChatNotificationPrompt = () => {
  const [permission, setPermission] = useState(() => getPushNotificationPermission());
  const [status, setStatus] = useState(() => {
    const initialPermission = getPushNotificationPermission();
    return initialPermission === 'granted' && isPushNotificationConfigured() ? 'checking' : 'idle';
  });
  const [hidden, setHidden] = useState(false);
  const isConfigured = isPushNotificationConfigured();

  useEffect(() => {
    let isMounted = true;

    if (permission === 'granted' && isConfigured) {
      registerCurrentDeviceForPush({ source: 'chat', prompt: false }).then((result) => {
        if (isMounted) {
          setStatus(result.success ? 'registered' : result.status || 'idle');
        }
      });
    }

    return () => {
      isMounted = false;
    };
  }, [isConfigured, permission]);

  const handleEnable = async () => {
    setStatus('saving');
    const result = await registerCurrentDeviceForPush({ source: 'chat', prompt: true });
    setPermission(getPushNotificationPermission());
    setStatus(result.success ? 'registered' : result.status || 'idle');
  };

  if (hidden || permission === 'unsupported' || status === 'registered' || status === 'checking') {
    return null;
  }

  const isMissingKey = !isConfigured || status === 'missing-vapid-key';
  const isDenied = permission === 'denied';

  return (
    <Collapse in={!hidden}>
      <Box sx={{ px: { xs: 1.5, sm: 0 }, pb: 1.5 }}>
        <Alert
          severity={isDenied || isMissingKey ? 'info' : 'success'}
          icon={<NotificationsActiveOutlinedIcon fontSize="inherit" />}
          onClose={() => setHidden(true)}
          sx={{
            borderRadius: 3,
            alignItems: 'center',
            '& .MuiAlert-message': { width: '100%' },
          }}
          action={
            permission === 'default' && !isMissingKey ? (
              <Button
                color="inherit"
                size="small"
                onClick={handleEnable}
                disabled={status === 'saving'}
                sx={{ textTransform: 'none', fontWeight: 800 }}
              >
                {status === 'saving' ? 'Turning on...' : 'Turn on'}
              </Button>
            ) : null
          }
        >
          {isMissingKey
            ? 'Chat notifications are ready in the app, but the Firebase web push key still needs to be configured.'
            : isDenied
              ? 'Chat notifications are blocked in this browser. You can turn them back on in browser settings.'
              : 'Turn on chat notifications so new care-circle messages do not get missed.'}
        </Alert>
      </Box>
    </Collapse>
  );
};

export default ChatNotificationPrompt;
